import React, { useContext, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';

import * as help from 'helpers';
import { ERRORS, PATHS } from 'config';
import { AuthStore, localesStore, UserStore } from 'stores';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import * as appsFlyer from 'tracking/appsFlyer';

import {
    Form,
    FormField,
    FormInput,
    Card,
    TextSeparator,
    Loader,
    FacebookAccess,
    AuthTerms,
    Alert,
    Button,
    Typography,
    FormPasswordField,
} from 'components';

import { CenteredDiv } from 'components/StyledCommon';

const PASSWORD_MIN_LENGTH = 8;

const SignupForm = ({ onLoginClick, onSignupDestination, ...props }) => {
    const authStore = useContext(AuthStore);
    const userStore = useContext(UserStore);
    const submitting = help.useBoolean(false);
    const { locale } = localesStore;
    const [error, setError] = useState('');

    const onSignupSuccess = () => {
        window.dataLayer.push({
            event: 'AccountCreated',
        });
    };

    const onFacebookAccessSuccess = async () => {
        await authStore.zendesk();
        onSignupSuccess();
        props.history.replace(onSignupDestination);
    };

    const onFacebookAccessError = (err) => {
        submitting.setFalse();
        err.status === 409
            ? setError(i18n._(ERRORS.facebook_already_signed_up))
            : setError(i18n._(ERRORS.facebook_unknown));
    };

    const handleSubmitForm = async (e) => {
        e && e.preventDefault();

        if (!validate() || submitting.value) {
            return;
        }

        submitting.setTrue();
        const data = getData();

        try {
            const res = await userStore.available(data.email);

            if (!res.available) {
                setFormError('email', i18n._(ERRORS.email_taken));
                submitting.setFalse();
                return;
            }

            authStore
                .register(data.email, data.password)
                .then(() => {
                    appsFlyer.recordEvent('AccountCreated');
                })
                .then(async () => {
                    await authStore.zendesk();
                    onSignupSuccess();
                    if (locale) await localesStore.setUserLocale(locale);
                    props.history.replace(
                        onSignupDestination === PATHS.dashboard
                            ? PATHS.download
                            : onSignupDestination,
                    );
                })
                .catch(() => {
                    // ToDo: show correct error message
                    setError(
                        i18n._(
                            t`There was an error while submitting your details.`,
                        ),
                    );
                    authStore.logout();
                    submitting.setFalse();
                });
        } catch (e) {}
    };

    const formInitialData = {
        email: '',
        password: '',
    };

    const formRules = {
        email: 'required|email',
        password: `required|min:${PASSWORD_MIN_LENGTH}`,
    };

    const formCustomMessages = {
        'email.required': i18n._(t`Please provide an email address`),
        'password.required': i18n._(t`The password is required to continue`),
    };

    const {
        form,
        errors,
        validate,
        getData,
        setError: setFormError,
    } = help.useValidatedForm(formInitialData, formRules, formCustomMessages);

    return (
        <Card noShadow mobileCard>
            {submitting.value ? (
                <Loader absolute={true} position="bottom" />
            ) : null}

            <FacebookAccess
                submitting={submitting.value}
                setSubmitting={submitting.setValue}
                onSuccess={onFacebookAccessSuccess}
                onError={onFacebookAccessError}
                label={i18n._(t`Sign up with Facebook`)}
                isLogin={false}
            />

            <TextSeparator>
                <Trans>or</Trans>
            </TextSeparator>

            {error ? (
                <Alert plain align="center" icon="alert" color="error">
                    {error}
                </Alert>
            ) : null}

            <Form onSubmit={handleSubmitForm}>
                <FormField
                    label={i18n._(t`Email address`)}
                    labelProps={{ htmlFor: 'form_email' }}
                    error={errors.email}
                >
                    <FormInput
                        {...form.email.input}
                        disabled={submitting.value}
                        placeholder=""
                        id="form_email"
                        tabIndex={0}
                        autoComplete="email"
                        type="email"
                        inputMode="email"
                    />
                </FormField>

                <FormField
                    label={i18n._(t`Password`)}
                    labelProps={{ htmlFor: 'form_password' }}
                    error={errors.password}
                >
                    <FormPasswordField
                        {...form.password.input}
                        disabled={submitting.value}
                        id="form_password"
                        tabIndex={0}
                        autoComplete="new-password"
                    />

                    {!errors.password && (
                        <Typography
                            variant="bodys"
                            spaceTop="tiny"
                            color="inactive"
                        >
                            <Trans>
                                The password must be at least{' '}
                                {PASSWORD_MIN_LENGTH} characters.
                            </Trans>
                        </Typography>
                    )}
                </FormField>

                <CenteredDiv topMargin>
                    <Button
                        data-action="submit"
                        type="submit"
                        theme={submitting.value ? 'inactive' : 'primary'}
                    >
                        <Trans>Create account</Trans>
                    </Button>
                </CenteredDiv>
            </Form>

            <CenteredDiv>
                <AuthTerms />

                <TextSeparator margin="medium" />

                <Typography variant="bodym">
                    <Trans>Already have an account?</Trans>
                </Typography>

                <Button
                    data-action="switch"
                    spaceTop="base"
                    theme="secondary"
                    onClick={onLoginClick ? onLoginClick : null}
                    to={help.route_with_params(PATHS.login)}
                    history={props.history}
                >
                    <Trans>Log in</Trans>
                </Button>
            </CenteredDiv>
        </Card>
    );
};

SignupForm.defaultProps = {
    onSignupDestination: PATHS.dashboard,
};

SignupForm.propTypes = {
    onLoginClick: PropTypes.func,
    onSignupDestination: PropTypes.string,
};

export default withRouter(observer(SignupForm));
